/**
 * Setting an address's risk from the case, rather than from its own page.
 *
 * The one judgement a transaction cannot carry. `PATCH /transactions/{id}`
 * forbids a risk field outright, because the platform propagates risk from
 * attribution — an entity or a label holds a `risk_level`, and everything
 * attributed to it inherits the reading. The address override is the escape
 * hatch for the case that has no catalog row yet: you have decided this
 * particular counterparty is bad, and you want that recorded before you have
 * decided who they are.
 *
 * Writes the tenancy override on the address row, the same field the address
 * edit dialog writes — this is a smaller door onto it, not a second store.
 */
import * as React from "react";
import { EnumSelect, Label, notifyError } from "@authe/ui-core";
import { RiskLevel } from "@authe/fe-commons/precedence";
import { useAddressesApi } from "@/api/adapters";
import { RISK_ORDER } from "@/features/addresses/shared";
import { useInvalidateAddressOverlays } from "@/features/overlays";

/** Every level the backend accepts, least to most severe. */
const RISK_OPTIONS = (Object.keys(RISK_ORDER) as RiskLevel[])
  .sort((a, b) => (RISK_ORDER[a] ?? 0) - (RISK_ORDER[b] ?? 0))
  .map((level) => ({ value: level, label: level[0]!.toUpperCase() + level.slice(1) }));

export interface AddressRiskControlProps {
  chainId: string;
  address: string;
  /** The saved level, or undefined when the row carries none. */
  level: RiskLevel | undefined;
  /** Re-read the address so the badge beside this shows what was written. */
  onSaved: () => void;
}

export function AddressRiskControl({
  chainId,
  address,
  level,
  onSaved,
}: AddressRiskControlProps): React.ReactElement | null {
  const addresses = useAddressesApi();
  const invalidateAddressOverlays = useInvalidateAddressOverlays();
  const [saving, setSaving] = React.useState(false);

  // Chain-scoped, like the row itself: the same address can be a house account
  // on one chain and a counterparty on another, and there is no row to write to
  // without knowing which.
  if (!chainId || !address) return null;

  const save = async (next: string): Promise<void> => {
    if (next === level) return;
    setSaving(true);
    try {
      await addresses.update(chainId, address, { risk_level: next });
      onSaved();
      // The panel's own row is what `onSaved` re-reads; this is everything else
      // drawing the same address — most visibly the graph node behind this
      // panel, which resolves its colour through a batch the host has no reason
      // to know about.
      invalidateAddressOverlays();
    } catch (error) {
      notifyError(error, { title: "Couldn't set the risk level" });
    } finally {
      setSaving(false);
    }
  };

  // Labelled through `htmlFor` rather than `aria-label`: `EnumSelect` does not
  // forward arbitrary attributes to its trigger, and TypeScript waves hyphenated
  // JSX attributes through — so an `aria-label` here would typecheck, render
  // nothing, and leave the control named only by whichever level it happens to
  // be showing.
  const id = `risk-${chainId}-${address}`;
  return (
    <span className="flex items-center">
      <Label htmlFor={id} className="sr-only">
        Risk level
      </Label>
      <EnumSelect
        id={id}
        value={level ?? ""}
        onChange={(next) => void save(next)}
        options={RISK_OPTIONS}
        placeholder="Set risk"
        disabled={saving}
        className="h-7 w-28 text-xs"
      />
    </span>
  );
}
